import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Paper,
  Card,
  CardContent
} from '@mui/material';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { changelogAPI } from '../services/apiWithAuth';

const GeneralHistoryDialog = ({ open, onClose, workTypes = [] }) => {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [hoursBack, setHoursBack] = useState(24);
  const [limit, setLimit] = useState(50);
  const [tableName, setTableName] = useState('');
  const [changedBy, setChangedBy] = useState('');

  // Fetch recent changes when dialog opens
  useEffect(() => {
    if (open) {
      fetchRecentChanges();
    }
  }, [open, hoursBack, limit, tableName, changedBy]);

  const fetchRecentChanges = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await changelogAPI.getRecentChanges(
        hoursBack, 
        limit, 
        tableName || null, 
        changedBy || null
      );
      setChanges(response.changes || []);
    } catch (err) {
      console.error('Error fetching recent changes:', err);
      setError('Nie udało się pobrać historii zmian');
    } finally {
      setLoading(false);
    }
  };

  const getOperationIcon = (operationType) => {
    switch (operationType) {
      case 'create':
        return <AddIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />;
      case 'update':
        return <EditIcon sx={{ color: '#2196f3', fontSize: '1.2rem' }} />;
      case 'delete':
        return <DeleteIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} />;
      default:
        return <HistoryIcon sx={{ color: '#757575', fontSize: '1.2rem' }} />;
    }
  };

  const getOperationColor = (operationType) => {
    switch (operationType) {
      case 'create':
        return 'success';
      case 'update':
        return 'primary';
      case 'delete':
        return 'error';
      default:
        return 'default';
    }
  };

  const getOperationBackground = (operationType) => {
    switch (operationType) {
      case 'create':
        return 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)';
      case 'update':
        return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
      case 'delete':
        return 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
      default:
        return 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)';
    }
  };

  const getOperationLabel = (operationType) => {
    switch (operationType) {
      case 'create':
        return 'Utworzono';
      case 'update':
        return 'Zmieniono';
      case 'delete':
        return 'Usunięto';
      default:
        return operationType;
    }
  };

  const getFieldLabel = (fieldName) => {
    switch (fieldName) {
      case 'work_type_code':
        return 'Typ pracy';
      case 'custom_text':
        return 'Tekst niestandardowy';
      case 'updated_by':
        return 'Zmienione przez';
      case 'employee_code':
        return 'Kod pracownika';
      default:
        return fieldName;
    }
  };

  const getWorkTypeName = (workTypeCode) => {
    if (!workTypeCode || workTypeCode === 'Del') return workTypeCode;
    
    // Debug log
    console.log('Szukam typu pracy:', workTypeCode, 'w:', workTypes);
    
    if (!workTypes || !Array.isArray(workTypes)) {
      return workTypeCode;
    }
    
    const workType = workTypes.find(wt => wt.code === workTypeCode);
    console.log('Znaleziony typ:', workType);
    
    return workType ? `${workType.code} - ${workType.name}` : workTypeCode;
  };

  const getChangeDescription = (change) => {
    const fieldLabel = getFieldLabel(change.field_name);
    const changedBy = change.changed_by || 'system';
    const operationType = (change.operation_type || '').toLowerCase();
    
    // Debug log dla sprawdzenia danych
    console.log('Change data:', change);
    
    if (operationType === 'create') {
      if (change.new_value) {
        return (
          <span>
            <strong>{changedBy}</strong> utworzył(a) wpis
            <br />
            <span style={{ color: '#388e3c', fontWeight: 'bold' }}>Nowa wartość {fieldLabel}:</span> {formatValue(change.new_value, change.field_name)}
          </span>
        );
      }
      return `${changedBy} utworzył(a) nowy wpis w polu ${fieldLabel}`;
    } 
    
    if (operationType === 'delete') {
      if (change.old_value) {
        return (
          <span>
            <strong>{changedBy}</strong> usunął(ęła) wpis
            <br />
            <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>Usunięto {fieldLabel}:</span> {formatValue(change.old_value, change.field_name)}
          </span>
        );
      }
      return `${changedBy} usunął(ęła) wpis w polu ${fieldLabel}`;
    } 
    
    if (operationType === 'update') {
      const oldVal = formatValue(change.old_value, change.field_name);
      const newVal = formatValue(change.new_value, change.field_name);
      
      // Specjalne formatowanie dla lepszej czytelności
      if (change.old_value !== null && change.new_value !== null) {
        return (
          <span>
            <strong>{changedBy}</strong> zmienił(a) <em>{fieldLabel}</em>
            <br />
            <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>Z:</span> {oldVal}
            <br />
            <span style={{ color: '#388e3c', fontWeight: 'bold' }}>Na:</span> {newVal}
          </span>
        );
      } else if (change.new_value !== null) {
        return (
          <span>
            <strong>{changedBy}</strong> ustawił(a) <em>{fieldLabel}</em>
            <br />
            <span style={{ color: '#388e3c', fontWeight: 'bold' }}>Nowa wartość:</span> {newVal}
          </span>
        );
      } else if (change.old_value !== null) {
        return (
          <span>
            <strong>{changedBy}</strong> usunął(ęła) wartość <em>{fieldLabel}</em>
            <br />
            <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>Poprzednia wartość:</span> {oldVal}
          </span>
        );
      }
    }
    
    // Fallback dla nieznanych operacji
    return `${changedBy} wykonał(a) operację: ${getOperationLabel(operationType)} na polu ${fieldLabel}`;
  };

  const getTableLabel = (tableName) => {
    switch (tableName) {
      case 'work_schedule_cells':
        return 'Harmonogram pracy';
      case 'employees':
        return 'Pracownicy';
      case 'service_schedules':
        return 'Harmonogram serwisów';
      case 'day_off_configs':
        return 'Konfiguracja dni wolnych';
      default:
        return tableName;
    }
  };

  const formatDateTime = (dateTimeString) => {
    try {
      const date = new Date(dateTimeString);
      return format(date, 'dd.MM.yyyy HH:mm:ss', { locale: pl });
    } catch (err) {
      return dateTimeString;
    }
  };

  const formatValue = (value, fieldName) => {
    // Sprawdź różne przypadki pustych wartości
    if (value === null || 
        value === undefined || 
        value === '' || 
        value === 'undefined' || 
        value === 'None' || 
        value === 'null') {
      return <em style={{ color: '#999' }}>brak</em>;
    }
    
    if (value === 'Del') {
      return <em style={{ color: '#ff6b6b' }}>usunięto</em>;
    }
    
    if (fieldName === 'work_type_code') {
      const workTypeName = getWorkTypeName(value);
      return <strong style={{ color: '#1976d2' }}>{workTypeName}</strong>;
    }
    
    if (fieldName === 'custom_text') {
      return <em style={{ color: '#7b1fa2' }}>"{value}"</em>;
    }
    
    return <span style={{ fontWeight: '500' }}>{value}</span>;
  };

  const handleRefresh = () => {
    fetchRecentChanges();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          <Typography variant="h6">
            Historia zmian w systemie
          </Typography>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Odśwież
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filtry:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Godziny wstecz"
                type="number"
                value={hoursBack}
                onChange={(e) => setHoursBack(parseInt(e.target.value) || 24)}
                inputProps={{ min: 1, max: 720 }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Limit wyników"
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 50)}
                inputProps={{ min: 10, max: 500 }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tabela</InputLabel>
                <Select
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  label="Tabela"
                >
                  <MenuItem value="">Wszystkie</MenuItem>
                  <MenuItem value="work_schedule_cells">Harmonogram pracy</MenuItem>
                  <MenuItem value="employees">Pracownicy</MenuItem>
                  <MenuItem value="service_schedules">Serwisy</MenuItem>
                  <MenuItem value="day_off_configs">Dni wolne</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Zmienione przez"
                value={changedBy}
                onChange={(e) => setChangedBy(e.target.value)}
                placeholder="np. user"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading spinner */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Statistics */}
            <Card sx={{ 
              mb: 3,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              color: 'white',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              border: '1px solid #1565c0'
            }}>
              <CardContent sx={{ pb: '16px !important' }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <HistoryIcon sx={{ fontSize: '20px' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Statystyki zmian
                  </Typography>
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip 
                    label={`Znaleziono: ${changes.length} zmian`}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      fontWeight: 500
                    }}
                    size="small"
                  />
                  <Chip 
                    label={`Okres: ${hoursBack}h`}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      fontWeight: 500
                    }}
                    size="small"
                  />
                  {tableName && (
                    <Chip 
                      label={`Tabela: ${getTableLabel(tableName)}`}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        fontWeight: 500
                      }}
                      size="small"
                    />
                  )}
                  {changedBy && (
                    <Chip 
                      icon={<PersonIcon sx={{ fontSize: '16px !important', color: 'white !important' }} />}
                      label={`Użytkownik: ${changedBy}`}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        fontWeight: 500
                      }}
                      size="small"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Changes list */}
            {changes.length === 0 ? (
              <Alert 
                severity="info"
                sx={{
                  borderRadius: 2,
                  border: '1px solid #e3f2fd',
                  backgroundColor: '#f3e5f5'
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Brak zmian w wybranym okresie
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Spróbuj zwiększyć zakres czasowy lub zmienić filtry.
                </Typography>
              </Alert>
            ) : (
              <>
                <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid #bdbdbd' }}>
                  <Typography variant="h6" sx={{ 
                    color: 'text.primary',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <HistoryIcon />
                    Ostatnie zmiany ({changes.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Chronologiczna lista wszystkich modyfikacji w systemie
                  </Typography>
                </Box>
                <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                  {changes.map((change, index) => (
                    <React.Fragment key={change.id}>
                      <ListItem 
                        alignItems="flex-start"
                        sx={{
                          background: getOperationBackground(change.operation_type),
                          borderRadius: 2,
                          mb: 1,
                          border: '1px solid #e0e0e0',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <Box display="flex" alignItems="flex-start" width="100%" gap={2} p={1}>
                          {/* Operation icon with circle background */}
                          <Box 
                            sx={{
                              mt: 0.5,
                              p: 1,
                              borderRadius: '50%',
                              backgroundColor: 'white',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {getOperationIcon(change.operation_type)}
                          </Box>

                          {/* Change details */}
                          <Box flex={1}>
                            {/* Header with operation type and timestamp */}
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Chip 
                                  label={getOperationLabel(change.operation_type)}
                                  color={getOperationColor(change.operation_type)}
                                  size="small"
                                  sx={{ 
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem'
                                  }}
                                />
                                <Chip 
                                  label={getTableLabel(change.table_name)}
                                  variant="outlined"
                                  size="small"
                                  sx={{ 
                                    fontSize: '0.7rem',
                                    height: '24px'
                                  }}
                                />
                                <Chip
                                  icon={<PersonIcon sx={{ fontSize: '16px !important' }} />}
                                  label={change.changed_by || 'System'}
                                  variant="outlined"
                                  size="small"
                                  sx={{ 
                                    fontSize: '0.7rem',
                                    height: '24px'
                                  }}
                                />
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <ScheduleIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {formatDateTime(change.changed_at)}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Record identification */}
                            <Box display="flex" alignItems="center" gap={2} mb={1} flexWrap="wrap">
                              {change.employee_code && (
                                <Box sx={{
                                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                  border: '1px solid rgba(33, 150, 243, 0.3)',
                                  borderRadius: 1,
                                  p: 0.5,
                                  px: 1
                                }}>
                                  <Typography variant="caption" sx={{ 
                                    color: '#1976d2',
                                    fontWeight: 'bold',
                                    fontSize: '0.65rem'
                                  }}>
                                    PRACOWNIK
                                  </Typography>
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: 500,
                                    color: '#1976d2'
                                  }}>
                                    {change.employee_code}
                                  </Typography>
                                </Box>
                              )}
                              {change.year && change.month && change.day && (
                                <Box sx={{
                                  backgroundColor: 'rgba(156, 39, 176, 0.1)',
                                  border: '1px solid rgba(156, 39, 176, 0.3)',
                                  borderRadius: 1,
                                  p: 0.5,
                                  px: 1
                                }}>
                                  <Typography variant="caption" sx={{ 
                                    color: '#7b1fa2',
                                    fontWeight: 'bold',
                                    fontSize: '0.65rem'
                                  }}>
                                    DATA
                                  </Typography>
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: 500,
                                    color: '#7b1fa2'
                                  }}>
                                    {change.day}.{change.month}.{change.year}
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Change description */}
                            <Typography variant="body2" sx={{ 
                              p: 1.5,
                              backgroundColor: 'white',
                              borderRadius: 1,
                              border: '1px solid #bdbdbd',
                              fontWeight: 500,
                              lineHeight: 1.4
                            }}>
                              {getChangeDescription(change)}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Zamknij
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeneralHistoryDialog;